<template>
  <aside class="control-sidebar" aria-label="Simulation controls">
    <header class="brand-row">
      <div>
        <div class="brand-mark"><Waves :size="19" /> OpenFloodLab</div>
        <div class="brand-subtitle">{{ labels.subtitle }}</div>
      </div>
      <button
        class="icon-button"
        type="button"
        :title="labels.language"
        @click="$emit('toggle-locale')"
      >
        <Languages :size="18" />
      </button>
    </header>

    <div class="control-section">
      <label class="field-label" for="dataset">{{ labels.dataset }}</label>
      <select id="dataset" :value="datasetId" @change="onDatasetChange">
        <option value="boulder-canyon-3dep">Boulder Canyon · USGS 3DEP</option>
        <option value="synthetic-basin">{{ labels.syntheticBasin }}</option>
      </select>
      <div class="dataset-meta">
        <Database :size="15" />
        <span>{{ datasetName || labels.loading }}</span>
      </div>
    </div>

    <div class="control-section control-grid">
      <label class="range-field">
        <span
          >{{ labels.radius }} <output>{{ radius }} m</output></span
        >
        <input
          type="range"
          min="20"
          max="600"
          step="10"
          :value="radius"
          @input="$emit('update:radius', numberValue($event))"
        />
      </label>
      <label class="range-field">
        <span
          >{{ labels.flow }} <output>{{ flowRate }} m³/s</output></span
        >
        <input
          type="range"
          min="500"
          max="50000"
          step="500"
          :value="flowRate"
          @input="$emit('update:flow-rate', numberValue($event))"
        />
      </label>
      <label class="range-field">
        <span
          >{{ labels.speed }} <output>{{ speed }}×</output></span
        >
        <input
          type="range"
          min="1"
          max="100"
          step="1"
          :value="speed"
          @input="$emit('update:speed', numberValue($event))"
        />
      </label>
      <label class="range-field">
        <span
          >{{ labels.threshold }} <output>{{ threshold.toFixed(3) }} m</output></span
        >
        <input
          type="range"
          min="0.001"
          max="0.5"
          step="0.001"
          :value="threshold"
          @input="$emit('update:threshold', numberValue($event))"
        />
      </label>
    </div>

    <div class="control-section command-grid">
      <button
        type="button"
        class="primary-command"
        :disabled="!ready"
        @click="$emit('add-default')"
      >
        <MapPinPlus :size="17" /> {{ labels.addSource }}
      </button>
      <button
        type="button"
        :class="{ active: clickPlacement }"
        :disabled="!ready"
        @click="$emit('toggle-placement')"
      >
        <Crosshair :size="17" />
        {{ clickPlacement ? labels.clickToPlace : labels.mapPlacement }}
      </button>
      <button type="button" :disabled="!ready" @click="$emit('toggle-pause')">
        <Play v-if="paused" :size="17" />
        <Pause v-else :size="17" />
        {{ paused ? labels.resume : labels.pause }}
      </button>
      <button type="button" :disabled="!ready" @click="$emit('reset')">
        <RotateCcw :size="17" /> {{ labels.reset }}
      </button>
      <button type="button" :disabled="sourceCount === 0" @click="$emit('clear')">
        <Trash2 :size="17" /> {{ labels.clearSources }}
      </button>
    </div>

    <div v-if="sources.length > 0" class="control-section source-section">
      <div class="section-heading">{{ labels.activeSources }}</div>
      <div class="source-list">
        <div v-for="(source, index) in sources" :key="source.id" class="source-row">
          <div class="source-row-heading">
            <span><MapPin :size="14" /> {{ labels.sourceLabel }} {{ index + 1 }}</span>
            <button
              type="button"
              class="icon-button source-delete"
              :title="labels.removeSource"
              :data-testid="`remove-source-${index}`"
              @click="$emit('remove-source', source.id)"
            >
              <Trash2 :size="15" />
            </button>
          </div>
          <div class="source-fields">
            <label>
              <span>{{ labels.flowShort }} (m³/s)</span>
              <input
                type="number"
                min="0"
                max="50000"
                step="500"
                :data-testid="`source-flow-${index}`"
                :value="source.flowRateCubicMetersPerSecond"
                @input="
                  $emit('update-source', source.id, {
                    flowRateCubicMetersPerSecond: numberValue($event)
                  })
                "
              />
            </label>
            <label>
              <span>{{ labels.radiusShort }} (m)</span>
              <input
                type="number"
                min="20"
                max="600"
                step="10"
                :data-testid="`source-radius-${index}`"
                :value="source.radiusMeters"
                @input="$emit('update-source', source.id, { radiusMeters: numberValue($event) })"
              />
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="control-section">
      <div class="section-heading">{{ labels.exports }}</div>
      <div class="export-row">
        <button
          type="button"
          :title="labels.depth"
          :disabled="!ready"
          @click="$emit('export', 'depth')"
        >
          <Download :size="17" /> {{ labels.depth }}
        </button>
        <button
          type="button"
          :title="labels.extent"
          :disabled="!ready"
          @click="$emit('export', 'extent')"
        >
          <Scan :size="17" /> {{ labels.extent }}
        </button>
        <button
          type="button"
          :title="labels.report"
          :disabled="!ready"
          @click="$emit('export', 'report')"
        >
          <FileJson :size="17" /> {{ labels.report }}
        </button>
      </div>
    </div>

    <div class="control-section status-section">
      <div class="section-heading">{{ labels.runStatus }}</div>
      <dl class="metrics">
        <div>
          <dt>{{ labels.elapsed }}</dt>
          <dd>{{ formatDuration(statistics.elapsedSeconds, labels) }}</dd>
        </div>
        <div>
          <dt>{{ labels.wetCells }}</dt>
          <dd>{{ formatInteger(statistics.wetCellCount, locale) }}</dd>
        </div>
        <div>
          <dt>{{ labels.maxDepth }}</dt>
          <dd>{{ statistics.maximumDepthMeters.toFixed(3) }} m</dd>
        </div>
        <div>
          <dt>{{ labels.volume }}</dt>
          <dd>{{ compactNumber(statistics.waterVolumeCubicMeters, locale) }} m³</dd>
        </div>
      </dl>
      <div class="capability-row">
        <span :class="{ ok: webgl2 }">WebGL2</span>
        <span :class="{ ok: floatFramebuffer }">Float FBO</span>
        <span :class="{ ok: ready }">DEM</span>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import type { InflowSource, SimulationStatistics } from '@openfloodlab/simulation-core';
import {
  Crosshair,
  Database,
  Download,
  FileJson,
  Languages,
  MapPin,
  MapPinPlus,
  Pause,
  Play,
  RotateCcw,
  Scan,
  Trash2,
  Waves
} from '@lucide/vue';

defineProps<{
  labels: Record<string, string>;
  locale: 'en' | 'zh';
  datasetId: string;
  datasetName: string;
  radius: number;
  flowRate: number;
  speed: number;
  threshold: number;
  clickPlacement: boolean;
  paused: boolean;
  ready: boolean;
  sourceCount: number;
  sources: readonly InflowSource[];
  webgl2: boolean;
  floatFramebuffer: boolean;
  statistics: SimulationStatistics;
}>();

const emit = defineEmits<{
  'toggle-locale': [];
  'change-dataset': [id: string];
  'update:radius': [value: number];
  'update:flow-rate': [value: number];
  'update:speed': [value: number];
  'update:threshold': [value: number];
  'add-default': [];
  'toggle-placement': [];
  'toggle-pause': [];
  reset: [];
  clear: [];
  'update-source': [
    id: string,
    changes: Partial<Pick<InflowSource, 'radiusMeters' | 'flowRateCubicMetersPerSecond'>>
  ];
  'remove-source': [id: string];
  export: [type: 'depth' | 'extent' | 'report'];
}>();

function numberValue(event: Event): number {
  return Number((event.target as HTMLInputElement).value);
}

function onDatasetChange(event: Event): void {
  emit('change-dataset', (event.target as HTMLSelectElement).value);
}

function numberLocale(locale: 'en' | 'zh'): string {
  return locale === 'zh' ? 'zh-CN' : 'en-US';
}

function formatInteger(value: number, locale: 'en' | 'zh'): string {
  return new Intl.NumberFormat(numberLocale(locale), { maximumFractionDigits: 0 }).format(value);
}

function compactNumber(value: number, locale: 'en' | 'zh'): string {
  return new Intl.NumberFormat(numberLocale(locale), {
    notation: 'compact',
    maximumFractionDigits: 1
  }).format(value);
}

function formatDuration(seconds: number, labels: Record<string, string>): string {
  const totalSeconds = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  if (totalSeconds < 60) return `${totalSeconds.toFixed(1)} ${labels.secondsUnit}`;

  const wholeSeconds = Math.floor(totalSeconds);
  const hours = Math.floor(wholeSeconds / 3600);
  const minutes = Math.floor((wholeSeconds % 3600) / 60);
  const remainingSeconds = wholeSeconds % 60;
  if (hours === 0) {
    return `${minutes} ${labels.minutesUnit} ${remainingSeconds.toString().padStart(2, '0')} ${labels.secondsUnit}`;
  }
  return `${hours} ${labels.hoursUnit} ${minutes.toString().padStart(2, '0')} ${labels.minutesUnit} ${remainingSeconds.toString().padStart(2, '0')} ${labels.secondsUnit}`;
}
</script>
